// Excel (.xlsx) export for the ComparisonTable.
// Uses xlsx-js-style (SheetJS + cell styling) for reliable, styled output.
//
// The workbook prints what the comparison page shows (Drew, 2026-10-05): the call site
// hands over the flags it rendered under as an ExportView — the Cost Benefit block on
// Summary is the journey cards' figures or the paired bars' — and every figure comes
// from the same helpers the screen calls.

import {
  payoffHorizonYears, journeyHorizon, altGrossReturnMultiple, altAnnualizedReturn,
  buildStrategyJourney,
} from "@/tax/strategyJourney";
import { LEGACY_EXPORT_VIEW, type ExportView } from "@/lib/exportView";
import XLSX from "xlsx-js-style";
import type { TaxScenario, TaxScenarioInput } from "@/tax/types";
import { formatCurrency, formatPercent, formatMultiple, formatCompactCurrency } from "@/tax/format";
import { METRICS } from "@/lib/comparisonMetrics";
import { CYA_TEXT } from "@/lib/disclaimer";
import { pensionTotal } from "@/lib/pensionTotal";

// Brand colors
const NAVY = "2D4B6E";
const WHITE = "FFFFFF";
const HIGHLIGHT_BG = "E8F0FE";
const GREEN_BG = "E8F5E9";
const RED_BG = "FDE7E7";
const GREEN_FG = "2E7D32";
const RED_FG = "C62828";

/** Style presets */
const WRAP: XLSX.CellStyle = { alignment: { wrapText: true, vertical: "top" } };
const HEADER: XLSX.CellStyle = {
  font: { bold: true, color: { rgb: WHITE }, sz: 10 },
  fill: { fgColor: { rgb: NAVY } },
  alignment: { wrapText: true, vertical: "center", horizontal: "center" },
};
const BOLD_WRAP: XLSX.CellStyle = {
  font: { bold: true },
  alignment: { wrapText: true, vertical: "top" },
};
const HIGHLIGHT_WRAP: XLSX.CellStyle = {
  font: { bold: true },
  fill: { fgColor: { rgb: HIGHLIGHT_BG } },
  alignment: { wrapText: true, vertical: "top" },
};
const TITLE: XLSX.CellStyle = {
  font: { bold: true, sz: 14, color: { rgb: NAVY } },
  alignment: { wrapText: true, vertical: "center", horizontal: "center" },
};
const SUBTITLE: XLSX.CellStyle = {
  font: { sz: 10, color: { rgb: "666666" } },
  alignment: { wrapText: true, vertical: "center", horizontal: "center" },
};
const DISCLAIMER: XLSX.CellStyle = {
  font: { italic: true, sz: 8, color: { rgb: "666666" } },
  alignment: { wrapText: true, vertical: "top", horizontal: "left" },
};

/** Coerce to a finite number — prevents NaN/undefined in cells. */
function safeNum(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function sanitizeFilename(name: string): string {
  return name.replace(/[^a-zA-Z0-9 _-]/g, "").replace(/\s+/g, "-");
}

/**
 * Strips the client-name prefix from a scenario name for use in column headers.
 * e.g. "TEst 2 (Roth + Alt)" → "Roth + Alt". Baseline column gets "Baseline".
 * Mirrors the helper in pdfExport.ts; kept inline to avoid coupling the two
 * exporters through a new shared module.
 */
function shortStrategyName(fullName: string, _clientName: string, isBaseline: boolean): string {
  if (isBaseline) return "Baseline";
  // Match on-screen ComparisonTable.tsx's `strategySuffix` — grab last (...) group.
  // Resilient to any prefix drift (duplicated clients, renamed baselines, etc.).
  const m = fullName.match(/\(([^)]+)\)\s*$/);
  return m ? m[1] : fullName;
}

function fmtCurrency(v: number): string {
  return formatCurrency(v);
}

function fmtPercent(v: number): string {
  return formatPercent(v);
}

/** Build a styled cell. */
function cell(v: string, style?: XLSX.CellStyle): XLSX.CellObject {
  return { v, t: "s", s: style ?? WRAP };
}

/** Calculate column widths from data — max content length per column, capped. */
function autoColWidths(rows: XLSX.CellObject[][], min = 14, max = 44): XLSX.ColInfo[] {
  const colCount = Math.max(...rows.map((r) => r.length));
  const widths: number[] = Array(colCount).fill(min);
  for (const row of rows) {
    for (let c = 0; c < row.length; c++) {
      const len = String(row[c]?.v ?? "").length;
      const padded = len + 4; // extra padding so nothing is clipped
      if (padded > widths[c]) widths[c] = Math.min(padded, max);
    }
  }
  return widths.map((w) => ({ wch: w }));
}

const GOOD: XLSX.CellStyle = { font: { bold: true, color: { rgb: GREEN_FG } }, alignment: { wrapText: true, vertical: "top" } };
const BAD: XLSX.CellStyle = { font: { bold: true, color: { rgb: RED_FG } }, alignment: { wrapText: true, vertical: "top" } };
const signedCell = (v: number) => cell(`${v >= 0 ? "+" : "−"}${fmtCurrency(Math.abs(v))}`, v >= 0 ? GOOD : BAD);

// ── Sheet 1: Summary ─────────────────────────────────────────────────────────
function buildSummaryRows(scenarios: TaxScenario[], view: ExportView): { rows: XLSX.CellObject[][]; merges: XLSX.Range[] } {
  const baseScenario = scenarios[0];
  const clientName = baseScenario.input.name;
  const reportDate = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  const rows: XLSX.CellObject[][] = [];
  const totalCols = 5; // Strategy + 4 value columns
  const merges: XLSX.Range[] = [];

  // Title row — merged & centered across all columns
  const titleRow: XLSX.CellObject[] = [cell(`TaxGenie Comparison Report — ${scenarios[0].input.name}`, TITLE)];
  for (let i = 1; i < totalCols; i++) titleRow.push(cell("", TITLE));
  rows.push(titleRow);
  merges.push({ s: { r: 0, c: 0 }, e: { r: 0, c: totalCols - 1 } });

  // Date row — merged & centered
  const dateRow: XLSX.CellObject[] = [cell(`Generated ${reportDate}`, SUBTITLE)];
  for (let i = 1; i < totalCols; i++) dateRow.push(cell("", SUBTITLE));
  rows.push(dateRow);
  merges.push({ s: { r: 1, c: 0 }, e: { r: 1, c: totalCols - 1 } });

  rows.push([]); // blank row

  // Economic Difference section
  rows.push([cell("Est. Future Value Economic Difference", BOLD_WRAP)]);
  rows.push(["Strategy", "Net Benefit", "Tax Savings", "Alt Inv. Returns", "Future Taxes Avoided"].map((h) => cell(h, HEADER)));

  for (let i = 0; i < scenarios.length; i++) {
    const s = scenarios[i];
    if (i === 0) {
      rows.push([cell(shortStrategyName(s.input.name, clientName, true), BOLD_WRAP), cell("—"), cell("—"), cell("—"), cell("—")]);
      continue;
    }
    const taxSavings = safeNum(baseScenario.results.totalTaxLiability) - safeNum(s.results.totalTaxLiability);
    const investmentReturns = safeNum(s.results.hpeFutureValue) > 0
      ? safeNum(s.results.hpeFutureValue) - safeNum(s.input.alternativeInvestmentAmount) : 0;
    const futureTaxesAvoided = safeNum(s.results.estimatedFutureTaxAvoided);
    const netBenefit = taxSavings + investmentReturns + futureTaxesAvoided;

    rows.push([
      cell(shortStrategyName(s.input.name, clientName, false), WRAP),
      cell(fmtCurrency(netBenefit)),
      cell(fmtCurrency(taxSavings)),
      cell(fmtCurrency(investmentReturns)),
      cell(fmtCurrency(futureTaxesAvoided)),
    ]);
  }

  rows.push([]); // blank row
  rows.push([]); // extra blank row

  // Chart data section
  rows.push([cell("Chart Data", BOLD_WRAP)]);
  rows.push(["Scenario", "Total Tax", "Total Economic Cost"].map((h) => cell(h, HEADER)));
  scenarios.forEach((s, i) => {
    rows.push([
      cell(shortStrategyName(s.input.name, clientName, i === 0), WRAP),
      cell(fmtCurrency(safeNum(s.results.totalTaxLiability))),
      cell(fmtCurrency(safeNum(s.results.totalEconomicCost))),
    ]);
  });

  if (scenarios.length >= 2) appendCostBenefitRows(rows, scenarios, view);

  return { rows, merges };
}

// ── Cost Benefit block on Summary — the screen's card, in numbers ────────────
// Journey on: each card's header line (net out of pocket, back by Year N, breakeven,
// net benefit) and then the curve itself, year by year, from buildStrategyJourney.
// Journey off: the paired bars — Year 1 cost over baseline, Year N gross return, net.
function appendCostBenefitRows(rows: XLSX.CellObject[][], scenarios: TaxScenario[], view: ExportView) {
  const baseScenario = scenarios[0];
  const clientName = baseScenario.input.name;
  const strategies = scenarios.slice(1);
  const horizon = payoffHorizonYears(scenarios);
  const name = (s: TaxScenario) => shortStrategyName(s.input.name, clientName, false);

  rows.push([]);
  rows.push([]);
  rows.push([cell(`Cost Benefit Analysis — ${horizon} Year Horizon`, BOLD_WRAP)]);

  if (!view.journey) {
    rows.push(["Strategy", "Year 1 Cost Over Baseline", `Year ${horizon} Gross Return`, `Net Benefit (Year ${horizon})`].map((h) => cell(h, HEADER)));
    for (const s of strategies) {
      const yearNReturn = safeNum(s.results.hpeFutureValue) + safeNum(s.results.estimatedFutureTaxAvoided);
      const costOverBaseline = safeNum(s.results.totalEconomicCost) - safeNum(baseScenario.results.totalEconomicCost);
      rows.push([cell(name(s), WRAP), cell(fmtCurrency(costOverBaseline)), cell(fmtCurrency(yearNReturn)), signedCell(yearNReturn - costOverBaseline)]);
    }
    return;
  }

  const journeys = strategies.map((s) => buildStrategyJourney(s, baseScenario, horizon));
  rows.push(["Strategy", "Year 1", `Back by Yr ${horizon}`, "Breakeven", `Net Benefit (Year ${horizon})`].map((h) => cell(h, HEADER)));
  journeys.forEach((j, i) => {
    const costLabel =
      j.costOverBaseline >= 0
        ? `Net out of pocket ${formatCompactCurrency(j.costOverBaseline)}`
        : `Saves ${formatCompactCurrency(Math.abs(j.costOverBaseline))} in Year 1`;
    const breakevenLabel =
      j.breakevenYear === null
        ? `Not whole by Yr ${j.horizonYears}`
        : j.breakevenYear === 0
          ? "Ahead from day one"
          : `Whole again ~Yr ${Math.ceil(j.breakevenYear)}`;
    rows.push([cell(name(strategies[i]), WRAP), cell(costLabel), cell(fmtCurrency(j.grossReturn)), cell(breakevenLabel), signedCell(j.netBenefit)]);
  });

  rows.push([]);
  rows.push([cell("Position vs. doing nothing, year by year", BOLD_WRAP)]);
  rows.push(["Year", ...strategies.map(name)].map((h) => cell(h, HEADER)));
  for (let t = 0; t <= horizon; t++) {
    rows.push([cell(`Yr ${t}`, BOLD_WRAP), ...journeys.map((j) => signedCell(j.points[t]?.position ?? 0))]);
  }
}

// ── Sheet 2: Comparison ──────────────────────────────────────────────────────
function buildComparisonRows(scenarios: TaxScenario[]): { rows: XLSX.CellObject[][]; merges: XLSX.Range[] } {
  const baseScenario = scenarios[0];
  const clientName = baseScenario.input.name;
  const rows: XLSX.CellObject[][] = [];
  const merges: XLSX.Range[] = [];

  // Header
  const headerLabels = ["Metric"];
  scenarios.forEach((s, i) => {
    headerLabels.push(shortStrategyName(s.input.name, clientName, i === 0));
  });
  scenarios.slice(1).forEach((s) => {
    headerLabels.push(`Δ ${shortStrategyName(s.input.name, clientName, false)}`);
  });
  rows.push(headerLabels.map((h) => cell(h, HEADER)));

  const totalCols = headerLabels.length;
  const sectionStyle: XLSX.CellStyle = {
    font: { bold: true, color: { rgb: "4B5563" } },
    fill: { fgColor: { rgb: "EEEEEE" } },
    alignment: { wrapText: false, vertical: "center" },
  };
  const pushSectionRow = (label: string) => {
    const rowIdx = rows.length;
    const row: XLSX.CellObject[] = [cell(label, sectionStyle)];
    for (let i = 1; i < totalCols; i++) row.push(cell("", sectionStyle));
    rows.push(row);
    merges.push({ s: { r: rowIdx, c: 0 }, e: { r: rowIdx, c: totalCols - 1 } });
  };

  // Metric rows — hide optional metrics that are zero across all scenarios
  const visibleMetrics = METRICS.filter(m =>
    !m.optional || scenarios.some(s => m.getValue(s) !== 0)
  );
  const firstFutureKey = visibleMetrics.find((m) => m.group === "future")?.key;
  const growthYears = payoffHorizonYears(scenarios);

  pushSectionRow("Current Year");

  for (const m of visibleMetrics) {
    if (m.key === firstFutureKey) {
      pushSectionRow(`Projected at end of Year ${growthYears}`);
    }
    const rowStyle = m.highlight ? HIGHLIGHT_WRAP : WRAP;
    const labelStyle = m.highlight ? HIGHLIGHT_WRAP : BOLD_WRAP;
    const row: XLSX.CellObject[] = [cell(m.label, labelStyle)];
    const fmt = m.format === "currency" ? fmtCurrency : fmtPercent;

    for (const s of scenarios) {
      row.push(cell(fmt(safeNum(m.getValue(s))), rowStyle));
    }

    for (const s of scenarios.slice(1)) {
      const diff = safeNum(m.getValue(s)) - safeNum(m.getValue(baseScenario));
      if (Math.abs(diff) < 0.01) {
        row.push(cell("—", rowStyle));
      } else {
        const favorable =
          (m.direction === "lower-is-better" && diff < 0) ||
          (m.direction === "higher-is-better" && diff > 0);
        const deltaStyle: XLSX.CellStyle = {
          font: { bold: m.highlight, color: { rgb: favorable ? GREEN_FG : RED_FG } },
          fill: { fgColor: { rgb: favorable ? GREEN_BG : RED_BG } },
          alignment: { wrapText: true, vertical: "top" },
        };
        const prefix = diff > 0 ? "+" : "";
        row.push(cell(`${prefix}${fmt(diff)}`, deltaStyle));
      }
    }
    rows.push(row);
  }

  // Est. Future Value Economic Difference
  const fvRow: XLSX.CellObject[] = [cell("Est. Future Value Econ. Diff.", HIGHLIGHT_WRAP)];
  for (let i = 0; i < scenarios.length; i++) {
    const s = scenarios[i];
    if (i === 0) { fvRow.push(cell("—", HIGHLIGHT_WRAP)); continue; }
    const oopDiff = safeNum(s.results.totalEconomicCost) - safeNum(baseScenario.results.totalEconomicCost);
    const fvEconDiff = safeNum(s.results.hpeFutureValue) + safeNum(s.results.estimatedFutureTaxAvoided) - oopDiff;
    fvRow.push(cell(fmtCurrency(fvEconDiff), HIGHLIGHT_WRAP));
  }
  for (const s of scenarios.slice(1)) {
    const oopDiff = safeNum(s.results.totalEconomicCost) - safeNum(baseScenario.results.totalEconomicCost);
    const fvEconDiff = safeNum(s.results.hpeFutureValue) + safeNum(s.results.estimatedFutureTaxAvoided) - oopDiff;
    fvRow.push(cell(fmtCurrency(fvEconDiff), HIGHLIGHT_WRAP));
  }
  rows.push(fvRow);

  return { rows, merges };
}

// ── Sheet 3: Client Data ─────────────────────────────────────────────────────
interface InputField {
  label: string;
  key: keyof TaxScenarioInput;
  type: "currency" | "percent" | "boolean" | "text" | "number";
  category: string;
  /** A row worked out from other inputs rather than read from one field. */
  value?: (input: TaxScenarioInput) => number;
}

const INPUT_FIELDS: InputField[] = [
  // Filing & Personal
  { label: "Filing Status", key: "filingStatus", type: "text", category: "Filing & Personal" },
  { label: "State", key: "state", type: "text", category: "Filing & Personal" },
  { label: "Tax Year", key: "taxYear", type: "number", category: "Filing & Personal" },
  { label: "Taxpayer 65+", key: "tp65Plus", type: "boolean", category: "Filing & Personal" },
  { label: "Spouse 65+", key: "sp65Plus", type: "boolean", category: "Filing & Personal" },
  { label: "Taxpayer 55+", key: "tp55Plus", type: "boolean", category: "Filing & Personal" },
  { label: "Spouse 55+", key: "sp55Plus", type: "boolean", category: "Filing & Personal" },
  { label: "Taxpayer Blind", key: "tpBlind", type: "boolean", category: "Filing & Personal" },
  { label: "Spouse Blind", key: "spBlind", type: "boolean", category: "Filing & Personal" },
  { label: "NY Taxpayer 59½+", key: "nyTp59Half", type: "boolean", category: "Filing & Personal" },
  { label: "NY Spouse 59½+", key: "nySp59Half", type: "boolean", category: "Filing & Personal" },

  // Income
  { label: "Wages", key: "wages", type: "currency", category: "Income" },
  { label: "Spouse Wages", key: "spouseWages", type: "currency", category: "Income" },
  { label: "Tax-Exempt Interest", key: "taxExemptInterest", type: "currency", category: "Income" },
  { label: "Interest Income", key: "interestIncome", type: "currency", category: "Income" },
  { label: "Ordinary Dividends", key: "ordinaryDividends", type: "currency", category: "Income" },
  { label: "Qualified Dividends", key: "qualifiedDividends", type: "currency", category: "Income" },
  { label: "IRA Distribution/Conversion", key: "iraDistributionConversion", type: "currency", category: "Income" },
  { label: "Roth Conversion Amount", key: "rothConversionAmount", type: "currency", category: "Income" },
  { label: "Gross Social Security", key: "grossSocialSecurity", type: "currency", category: "Income" },
  // The stored pensionSum field is never written; the row is the same five-box
  // total the Income step shows.
  { label: "Pension Sum", key: "pensionSum", type: "currency", category: "Income", value: pensionTotal },
  { label: "Military Retirement Pension", key: "militaryRetirementPension", type: "currency", category: "Income" },
  { label: "Non-Qualified Annuity Income", key: "nonQualifiedAnnuityIncome", type: "currency", category: "Income" },
  { label: "Short-Term Capital Gains", key: "shortTermCapitalGains", type: "currency", category: "Income" },
  { label: "Long-Term Capital Gains", key: "longTermCapitalGains", type: "currency", category: "Income" },
  { label: "Other Income (Sch 1 Part I)", key: "otherIncome", type: "currency", category: "Income" },

  // Schedule C
  { label: "Sched C Gross Receipts", key: "schedCGrossReceipts", type: "currency", category: "Schedule C" },
  { label: "Sched C Total Expenses", key: "schedCTotalExpenses", type: "currency", category: "Schedule C" },
  { label: "Spouse Sched C Gross Receipts", key: "spouseSchedCGrossReceipts", type: "currency", category: "Schedule C" },
  { label: "Spouse Sched C Total Expenses", key: "spouseSchedCTotalExpenses", type: "currency", category: "Schedule C" },

  // Schedule E
  { label: "Rental Active", key: "rentalActive", type: "boolean", category: "Schedule E" },
  { label: "Rental Income", key: "rentalIncome", type: "currency", category: "Schedule E" },
  { label: "Rental Total Expenses", key: "rentalTotalExpenses", type: "currency", category: "Schedule E" },
  { label: "K-1 Ordinary Business Income", key: "k1OrdinaryBusinessIncome", type: "currency", category: "Schedule E" },
  { label: "K-1 Guaranteed Payments", key: "k1GuaranteedPayments", type: "currency", category: "Schedule E" },
  { label: "K-1 Rental Real Estate Income", key: "k1RentalRealEstateIncome", type: "currency", category: "Schedule E" },
  { label: "K-1 Other Income", key: "k1OtherIncome", type: "currency", category: "Schedule E" },

  // Schedule A - Itemized Deductions
  { label: "Medical Expenses", key: "medicalExpenses", type: "currency", category: "Itemized Deductions" },
  { label: "SALT - State/Local", key: "saltStateLocal", type: "currency", category: "Itemized Deductions" },
  { label: "SALT - Real Estate", key: "saltRealEstate", type: "currency", category: "Itemized Deductions" },
  { label: "SALT - Personal Property", key: "saltPersonalProperty", type: "currency", category: "Itemized Deductions" },
  { label: "Home Mortgage Interest", key: "homeMortgageInterest", type: "currency", category: "Itemized Deductions" },
  { label: "Mortgage Points", key: "mortgagePoints", type: "currency", category: "Itemized Deductions" },
  { label: "Investment Interest", key: "investmentInterest", type: "currency", category: "Itemized Deductions" },
  { label: "Cash Gifts - Public Charity", key: "cashGiftsPublicCharity", type: "currency", category: "Itemized Deductions" },
  { label: "Non-Cash Property - Public Charity", key: "nonCashPropertyPublicCharity", type: "currency", category: "Itemized Deductions" },
  { label: "Property Gifts - Private Foundation", key: "propertyGiftsPrivateFoundation", type: "currency", category: "Itemized Deductions" },
  { label: "Capital Gain Property - Public Charity", key: "capitalGainPropertyPublicCharity", type: "currency", category: "Itemized Deductions" },
  { label: "Capital Gain Property - Private Foundation", key: "capitalGainPropertyPrivateFoundation", type: "currency", category: "Itemized Deductions" },
  { label: "Other Itemized Deductions", key: "otherItemizedDeductions", type: "currency", category: "Itemized Deductions" },

  // Schedule SE
  { label: "W-2 Social Security Wages", key: "w2SocialSecurityWages", type: "currency", category: "SE Tax" },
  { label: "Spouse W-2 Social Security Wages", key: "spouseW2SocialSecurityWages", type: "currency", category: "SE Tax" },

  // Adjustments
  { label: "Self-Employed Health Insurance", key: "selfEmployedHealthInsurance", type: "currency", category: "Adjustments" },
  { label: "Self-Employed Retirement", key: "selfEmployedRetirement", type: "currency", category: "Adjustments" },
  { label: "Educator Expenses", key: "educatorExpenses", type: "currency", category: "Adjustments" },
  { label: "HSA Deduction", key: "hsaDeduction", type: "currency", category: "Adjustments" },
  { label: "Traditional IRA Deduction", key: "traditionalIRADeduction", type: "currency", category: "Adjustments" },
  { label: "Student Loan Interest", key: "studentLoanInterest", type: "currency", category: "Adjustments" },
  { label: "Other Adjustments", key: "otherAdjustments", type: "currency", category: "Adjustments" },

  // AMT Adjustments
  { label: "AMT Adjustment", key: "amtAdjustment", type: "currency", category: "AMT" },
  { label: "Excess APTC Repayment", key: "excessAPTCRepayment", type: "currency", category: "AMT" },
  { label: "Other Additional Taxes", key: "otherAdditionalTaxes", type: "currency", category: "AMT" },
  { label: "Sched C AMT Depreciation", key: "schedCAmtDepreciation", type: "currency", category: "AMT" },
  { label: "K-1 AMT Ordinary Income Adj", key: "k1AmtOrdinaryIncomeAdj", type: "currency", category: "AMT" },
  { label: "K-1 AMT Depreciation Adj", key: "k1AmtDepreciationAdj", type: "currency", category: "AMT" },
  { label: "K-1 AMT Sec 179 Adj", key: "k1Amt179Adj", type: "currency", category: "AMT" },
  { label: "K-1 AMT Passive Loss Adj", key: "k1AmtPassiveLossAdj", type: "currency", category: "AMT" },
  { label: "K-1 AMT Depletion Adj", key: "k1AmtDepletionAdj", type: "currency", category: "AMT" },
  { label: "K-1 AMT IDC Adj", key: "k1AmtIDCAdj", type: "currency", category: "AMT" },
  { label: "K-1 AMT Other Adj", key: "k1AmtOtherAdj", type: "currency", category: "AMT" },
  { label: "Optional Passive Activity Addback", key: "optionalPassiveActivityAddback", type: "currency", category: "AMT" },

  // Credits
  { label: "Foreign Tax Credit", key: "foreignTaxCredit", type: "currency", category: "Credits" },
  { label: "Child/Dependent Care Credit", key: "childDependentCareCredit", type: "currency", category: "Credits" },
  { label: "Education Credits", key: "educationCredits", type: "currency", category: "Credits" },
  { label: "Saver's Credit", key: "saversCredit", type: "currency", category: "Credits" },
  { label: "Energy Credits", key: "energyCredits", type: "currency", category: "Credits" },
  { label: "Earned Income Credit", key: "earnedIncomeCredit", type: "currency", category: "Credits" },
  { label: "Additional Child Tax Credit", key: "additionalChildTaxCredit", type: "currency", category: "Credits" },
  { label: "AOTC Refundable", key: "aotcRefundable", type: "currency", category: "Credits" },
  { label: "Net Premium Tax Credit", key: "netPremiumTaxCredit", type: "currency", category: "Credits" },
  { label: "Amount Paid with Extension", key: "amountPaidWithExtension", type: "currency", category: "Credits" },
  { label: "Other Refundable Credits", key: "otherRefundableCredits", type: "currency", category: "Credits" },
  { label: "Federal Withholding", key: "federalWithholding", type: "currency", category: "Credits" },
  { label: "Estimated Tax Payments", key: "estimatedTaxPayments", type: "currency", category: "Credits" },
  { label: "State Withholding", key: "stateWithholding", type: "currency", category: "Credits" },

  // Flags
  { label: "Apply ACA", key: "applyACA", type: "boolean", category: "Flags" },
  { label: "Apply NIIT", key: "applyNIIT", type: "boolean", category: "Flags" },
  { label: "Number of Qualifying Children", key: "numberOfQualifyingChildren", type: "number", category: "Flags" },

  // QBI
  { label: "QBI W-2 Wages", key: "qbiW2Wages", type: "currency", category: "QBI" },
  { label: "QBI UBIA", key: "qbiUBIA", type: "currency", category: "QBI" },
  { label: "QBI SSTB", key: "qbiSSTB", type: "boolean", category: "QBI" },

  // Strategy
  { label: "Alternative Investment Amount", key: "alternativeInvestmentAmount", type: "currency", category: "Strategy" },
  { label: "Roth Growth Rate", key: "rothGrowthRate", type: "percent", category: "Strategy" },
  { label: "Roth Growth Years", key: "rothGrowthYears", type: "number", category: "Strategy" },
  { label: "HPE Deduction Multiplier", key: "hpeDeductionMultiplier", type: "number", category: "Strategy" },

  // Pension Split
  { label: "Govt Pension - Taxpayer", key: "govtPensionTP", type: "currency", category: "Pension Split" },
  { label: "Govt Pension - Spouse", key: "govtPensionSP", type: "currency", category: "Pension Split" },
  { label: "Non-Govt Pension - Taxpayer", key: "nonGovtPensionTP", type: "currency", category: "Pension Split" },
  { label: "Non-Govt Pension - Spouse", key: "nonGovtPensionSP", type: "currency", category: "Pension Split" },
];

function buildClientRows(scenarios: TaxScenario[]): XLSX.CellObject[][] {
  const clientName = scenarios[0].input.name;
  const rows: XLSX.CellObject[][] = [];

  // Header
  rows.push(["Category", "Field", ...scenarios.map((s, i) => shortStrategyName(s.input.name, clientName, i === 0))].map((h) => cell(h, HEADER)));

  let lastCategory = "";

  for (const field of INPUT_FIELDS) {
    const row: XLSX.CellObject[] = [];

    if (field.category !== lastCategory) {
      row.push(cell(field.category, BOLD_WRAP));
      lastCategory = field.category;
    } else {
      row.push(cell(""));
    }

    row.push(cell(field.label, WRAP));

    for (const s of scenarios) {
      const rawVal = field.value ? field.value(s.input) : s.input[field.key];
      if (field.type === "boolean") {
        row.push(cell(rawVal ? "Yes" : "No"));
      } else if (field.type === "currency") {
        row.push(cell(fmtCurrency(safeNum(rawVal))));
      } else if (field.type === "percent") {
        row.push(cell(fmtPercent(safeNum(rawVal))));
      } else if (field.type === "number") {
        row.push(cell(String(safeNum(rawVal))));
      } else {
        row.push(cell(String(rawVal ?? "")));
      }
    }

    rows.push(row);
  }

  return rows;
}

// ── Sheet 4: Alt Key Takeaways ───────────────────────────────────────────────
// Mirrors the on-screen ComparisonTable + PDF "Alt Key Takeaways" summary.
// hpeFutureValue is the fund exhibit's total cash to the client, scaled to the investment.
function buildPaybackRows(scenarios: TaxScenario[]): XLSX.CellObject[][] | null {
  const baseScenario = scenarios[0];
  const clientName = baseScenario.input.name;
  const hpeScenarios = scenarios.filter((s) => s.input.alternativeInvestmentAmount > 0);
  if (hpeScenarios.length === 0) return null;
  const periodYears = Math.max(...hpeScenarios.map(journeyHorizon));

  const rows: XLSX.CellObject[][] = [];

  // Header
  rows.push(["Metric", ...hpeScenarios.map((s) => shortStrategyName(s.input.name, clientName, false))].map((h) => cell(h, HEADER)));

  // Summary
  const summaryDefs: [string, (s: TaxScenario) => string][] = [
    ["Investment", (s) => fmtCurrency(safeNum(s.input.alternativeInvestmentAmount))],
    ["Total Gross Return", (s) => fmtCurrency(safeNum(s.results.hpeFutureValue))],
    [`Gross Return (${periodYears}-Yr)`, (s) => formatMultiple(altGrossReturnMultiple(s))],
    [`Annualized Return (${periodYears}-Yr)`, (s) => fmtPercent(altAnnualizedReturn(s))],
    ["Tax Savings", (s) => fmtCurrency(safeNum(baseScenario.results.totalTaxLiability) - safeNum(s.results.totalTaxLiability))],
  ];

  if (hpeScenarios.some((s) => s.results.estimatedFutureTaxAvoided > 0)) {
    summaryDefs.push(["Est. Future Taxes Avoided", (s) => safeNum(s.results.estimatedFutureTaxAvoided) > 0 ? fmtCurrency(safeNum(s.results.estimatedFutureTaxAvoided)) : "—"]);
  }

  for (const [label, fn] of summaryDefs) {
    rows.push([cell(label, HIGHLIGHT_WRAP), ...hpeScenarios.map((s) => cell(fn(s)))]);
  }

  // Net Benefit row — highlighted with green value
  const netBenefitStyle: XLSX.CellStyle = {
    font: { bold: true, color: { rgb: GREEN_FG } },
    fill: { fgColor: { rgb: HIGHLIGHT_BG } },
    alignment: { wrapText: true, vertical: "top" },
  };
  rows.push([
    cell("Net Benefit", HIGHLIGHT_WRAP),
    ...hpeScenarios.map((s) => {
      const inv = safeNum(s.input.alternativeInvestmentAmount);
      const taxSavings = safeNum(baseScenario.results.totalTaxLiability) - safeNum(s.results.totalTaxLiability);
      const netBenefit = taxSavings + safeNum(s.results.hpeFutureValue) - inv + safeNum(s.results.estimatedFutureTaxAvoided);
      return cell(fmtCurrency(netBenefit), netBenefitStyle);
    }),
  ]);

  return rows;
}

/** Convert a CellObject[][] into a worksheet with styles and auto-sized columns. */
/**
 * Stamps the CYA disclaimer as the last row of a sheet — the workbook's mirror of
 * the PDF's per-page footer, so a sheet pulled out and sent on its own still carries
 * it. Merged across the sheet's width; returns the row it landed on.
 */
function appendCYAFooter(rows: XLSX.CellObject[][], merges: XLSX.Range[]): number {
  const totalCols = Math.max(1, ...rows.map((r) => r.length));
  rows.push([]);
  const rowIdx = rows.length;
  const footer: XLSX.CellObject[] = [cell(CYA_TEXT, DISCLAIMER)];
  for (let i = 1; i < totalCols; i++) footer.push(cell("", DISCLAIMER));
  rows.push(footer);
  merges.push({ s: { r: rowIdx, c: 0 }, e: { r: rowIdx, c: totalCols - 1 } });
  return rowIdx;
}

function rowsToSheet(rows: XLSX.CellObject[][], merges?: XLSX.Range[]): XLSX.WorkSheet {
  // Widths come from the content rows only — the disclaimer is one long line and
  // would otherwise stretch column A to the cap on every sheet.
  const cols = autoColWidths(rows);

  const allRows = [...rows];
  const allMerges = merges ? [...merges] : [];
  const footerRow = appendCYAFooter(allRows, allMerges);

  // Build a plain aoa so SheetJS sets up the sheet dimensions, then overlay styles.
  const plainAoa = allRows.map((r) => r.map((c) => c?.v ?? ""));
  const ws = XLSX.utils.aoa_to_sheet(plainAoa);

  // Overlay cell styles
  for (let r = 0; r < allRows.length; r++) {
    for (let c = 0; c < allRows[r].length; c++) {
      const addr = XLSX.utils.encode_cell({ r, c });
      const wsCell = ws[addr];
      const srcCell = allRows[r][c];
      if (wsCell && srcCell?.s) {
        wsCell.s = srcCell.s;
      }
    }
  }

  ws["!cols"] = cols;
  // Merged + wrapped text doesn't auto-grow row height in Excel, so the disclaimer
  // row gets explicit room for its wrapped lines.
  const rowInfo = allRows.map(() => ({}) as XLSX.RowInfo);
  rowInfo[footerRow] = { hpt: 44 };
  ws["!rows"] = rowInfo;
  if (allMerges.length > 0) {
    ws["!merges"] = allMerges;
  }
  return ws;
}

// ── Main Export Function ─────────────────────────────────────────────────────
export async function generateComparisonExcel(scenarios: TaxScenario[], view: ExportView = LEGACY_EXPORT_VIEW): Promise<void> {
  const wb = XLSX.utils.book_new();

  // Summary — carries the Cost Benefit block the screen shows (journey cards or bars)
  const summary = buildSummaryRows(scenarios, view);
  const summaryWs = rowsToSheet(summary.rows, summary.merges);
  XLSX.utils.book_append_sheet(wb, summaryWs, "Summary");

  // Comparison
  const { rows: comparisonRows, merges: comparisonMerges } = buildComparisonRows(scenarios);
  const comparisonWs = rowsToSheet(comparisonRows, comparisonMerges);
  XLSX.utils.book_append_sheet(wb, comparisonWs, "Comparison");

  // Client Data
  const clientWs = rowsToSheet(buildClientRows(scenarios));
  XLSX.utils.book_append_sheet(wb, clientWs, "Client Data");

  // Alt Key Takeaways (optional)
  const paybackRows = buildPaybackRows(scenarios);
  if (paybackRows) {
    const paybackWs = rowsToSheet(paybackRows);
    XLSX.utils.book_append_sheet(wb, paybackWs, "Alt Key Takeaways");
  }

  const clientName = sanitizeFilename(scenarios[0].input.name);
  const dateStr = new Date().toISOString().slice(0, 10);
  XLSX.writeFile(wb, `TaxGenie-${clientName}-${dateStr}.xlsx`);
}
